import { createPopper, Instance, OptionsGeneric, Placement, State as PopperState } from '@popperjs/core';
import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { MenuItem } from '../menu-item/menu-item.interface';
import {
  deselectAllFromElements,
  getDataFromElements,
  getSelectedFromElements,
  removeSelectedFromElements,
  setSelectedFromElements,
  toggleSelectedFromElements,
} from './menu.html.utils';

@Component({
  tag: 'o2-menu',
  styleUrl: 'menu.scss',
  shadow: true,
})
export class O2Menu {
  private popperInstance: Instance;

  // default options
  private options: Partial<OptionsGeneric<any>> = {
    placement: 'bottom',
    modifiers: [
      {
        name: 'checkPlacement',
        enabled: true,
        phase: 'main',
        fn: ({ state }) => {
          this.renderedPlacement = state.placement;
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
    ],
  };

  private hasHtmlItems = false;

  private toggleEventHandler: () => void;

  @Element() el: HTMLO2MenuElement;

  @State() data: Array<MenuItem> = [];

  @State() renderedPlacement: Placement;

  @Prop({ reflect: true }) name: string;

  @Prop({ reflect: true, mutable: true }) open?: boolean = false;

  @Prop({ reflect: true }) for?: string;

  @Prop({ reflect: true }) multiselect?: boolean = false;

  /**
   * If `true`, the user cannot interact with the menu.
   */
  @Prop({ reflect: true }) disabled? = false;

  /**
   * Emitted when the menu has opened.
   */
  @Event() o2Open!: EventEmitter<any>;

  /**
   * Emitted when the menu has closed.
   */
  @Event() o2Close!: EventEmitter<any>;

  /**
   * Emitted when the selected menu-items has changed.
   */
  @Event() o2Change!: EventEmitter<MenuItem | MenuItem[]>;

  @Listen('click', { target: 'document' })
  handleDocumentClick(event: Event) {
    const targetElement = event.target as HTMLElement;
    const triggerElement = this.el.parentElement.querySelector(`#${this.for}`);

    if (this.open && !triggerElement.contains(targetElement) && !this.el.contains(targetElement)) {
      this.hide();
    }
  }

  @Listen('o2MenuItemClicked')
  async handleMenuItemClicked(event: CustomEvent<MenuItem>) {
    if (!this.multiselect) {
      await this.deselectAll();

      if (event.detail.selected) {
        await this.toggleSelected(event.detail.value);
      }
    } else {
      await this.toggleSelected(event.detail.value);
    }
  }

  @Listen('o2MenuItemSelected')
  async handleMenuItemSelected() {
    if (!this.multiselect) {
      this.hide();
    }
  }

  /**
   * Listens to keyboard arrows and enter key to navigate through the list.
   */
  @Listen('keydown')
  protected handleKeyDown(event: KeyboardEvent) {
    const isArrowKey = ['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key);

    if (isArrowKey) {
      event.preventDefault();

      let activeItem: HTMLO2MenuItemElement;
      let firstItem: HTMLO2MenuItemElement;
      let lastItem: HTMLO2MenuItemElement;

      if (this.hasHtmlItems) {
        activeItem = this.el.querySelector('o2-menu-item[active]');
        firstItem = this.el.querySelector('o2-menu-item');
        lastItem = this.el.querySelector('o2-menu-item:last-child');
      } else {
        activeItem = this.el.shadowRoot.querySelector('o2-menu-item[active]');
        firstItem = this.el.shadowRoot.querySelector('o2-menu-item');
        lastItem = this.el.shadowRoot.querySelector('o2-menu-item:last-child');
      }

      if (event.key === 'ArrowDown') {
        if (activeItem) {
          if (activeItem === lastItem) {
            activeItem.removeAttribute('active');
            firstItem.setAttribute('active', '');
          } else {
            activeItem.removeAttribute('active');
            activeItem.nextElementSibling.setAttribute('active', '');
          }
        } else {
          firstItem.setAttribute('active', '');
        }
      }

      if (event.key === 'ArrowUp') {
        if (activeItem) {
          if (activeItem === firstItem) {
            activeItem.removeAttribute('active');
            lastItem.setAttribute('active', '');
          } else {
            activeItem.removeAttribute('active');
            activeItem.previousElementSibling.setAttribute('active', '');
          }
        } else {
          lastItem.setAttribute('active', '');
        }
      }

      if (event.key === 'Enter') {
        const isSelected = activeItem.getAttribute('selected');

        // TODO: check .click() call possiblity
        if (isSelected !== null) {
          activeItem.removeAttribute('selected');
        } else {
          activeItem.setAttribute('selected', '');
        }
      }
    }

    if (this.open && event.key === 'Escape') {
      event.stopPropagation();
      this.hide();
    }
  }

  @Watch('open')
  protected openChangedHandler(newVal: boolean) {
    if (newVal) {
      this.o2Open.emit({});
    } else {
      this.o2Close.emit({});
    }
  }

  @Watch('disabled')
  protected disabledChangedHandler(newVal: boolean) {
    if (!newVal) {
      this.registerClickEventOnTriggerElement();
    } else {
      this.unregisterClickEventOnTriggerElement();
    }
  }

  @Method()
  async toggle() {
    this.open = !this.open;
  }

  @Method()
  async hide() {
    this.open = false;
  }

  @Method()
  async show() {
    this.open = true;
  }

  @Method()
  async setOptions(options: Partial<OptionsGeneric<any>>) {
    if (this.popperInstance) {
      this.popperInstance.setOptions(options);
    }
  }

  @Method()
  async update(): Promise<Partial<PopperState>> {
    if (this.popperInstance) {
      return await this.popperInstance.update();
    }
  }

  @Method()
  async getSelected(): Promise<MenuItem | MenuItem[] | undefined> {
    if (this.hasHtmlItems) {
      const selectedElements: MenuItem[] = getSelectedFromElements(this.el);

      if (this.multiselect) {
        return selectedElements;
      } else {
        return selectedElements.length ? selectedElements[0] : undefined;
      }
    } else {
      if (this.multiselect) {
        return this.data.filter(item => item.selected);
      } else {
        return this.data.find(item => item.selected);
      }
    }
  }

  @Method()
  async setSelected(value: string): Promise<void> {
    if (this.hasHtmlItems) {
      setSelectedFromElements(this.el, value);
    } else {
      this.data = this.data.map(item => ({
        ...item,
        selected: item.value === value ? true : item.selected,
      }));
    }

    this.o2Change.emit(await this.getSelected());
  }

  @Method()
  async toggleSelected(value: string): Promise<void> {
    if (this.hasHtmlItems) {
      toggleSelectedFromElements(this.el, value);
    } else {
      this.data = this.data.map(item => ({
        ...item,
        selected: item.value === value ? !item.selected : item.selected,
      }));
    }

    this.o2Change.emit(await this.getSelected());
  }

  @Method()
  async deselectAll(): Promise<void> {
    if (this.data.length > 0) {
      this.data = this.data.map(item => ({ ...item, selected: false }));
    } else {
      deselectAllFromElements(this.el);
    }

    this.o2Change.emit(await this.getSelected());
  }

  @Method()
  async removeSelected(value: string): Promise<void> {
    if (this.hasHtmlItems) {
      removeSelectedFromElements(this.el, value);
    } else {
      this.data = this.data.map(item => ({
        ...item,
        selected: item.value === value ? false : item.selected,
      }));
    }

    this.o2Change.emit(await this.getSelected());
  }

  @Method()
  async setData(data: Array<MenuItem>) {
    this.data = data;
  }

  @Method()
  async getData(): Promise<MenuItem[]> {
    return this.hasHtmlItems ? getDataFromElements(this.el) : this.data;
  }

  private registerClickEventOnTriggerElement = () => {
    const triggerElement = this.el.parentElement.querySelector(`#${this.for}`);

    if (triggerElement) {
      triggerElement.addEventListener('click', this.toggleEventHandler);
    }
  };

  private unregisterClickEventOnTriggerElement = () => {
    const triggerElement = this.el.parentElement.querySelector(`#${this.for}`);

    if (triggerElement) {
      triggerElement.removeEventListener('click', this.toggleEventHandler);
    }
  }

  private initPopper = () => {
    const externalElement = this.el.parentElement?.querySelector(`#${this.for}`);

    if (externalElement) {
      this.popperInstance = createPopper(externalElement, this.el, this.options);
    }
  };

  connectedCallback() {
    this.toggleEventHandler = this.toggle.bind(this);

    if (this.el.childNodes.length) {
      this.hasHtmlItems = true;
    }

    if (!this.disabled) {
      this.registerClickEventOnTriggerElement();
    }
  }

  componentDidRender() {
    this.update();
  }

  componentDidLoad() {
    this.initPopper();
  }

  render() {
    const hasData = this.el.childNodes.length > 0 || this.data.length > 0;

    return (
      <Host>
        <div class={`menu ${this.open && hasData ? 'menu--open' : ''} ${this.renderedPlacement ? `menu--shadow-${this.renderedPlacement}`: ''}`} role="menu">
          <slot></slot>
          {this.data.map(item => {
            return (
              <o2-menu-item value={item.value} text={item.text} selected={item.selected} multiselect={this.multiselect} name={this.name}>
                {item.html ? <div innerHTML={item.html}></div> : <span slot="text">{item.text}</span>}
              </o2-menu-item>
            );
          })}
        </div>
      </Host>
    );
  }
}
